import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { BeneficiaryDetail } from '@foundation/shared';
import { ApiRequestError, api } from '../lib/api';
import {
  BENEFICIARY_STATUS_TH,
  CATEGORY_TH,
  RISK_TH,
  ageFrom,
  formatDate,
  formatSatang,
  fullName,
  maskNationalId,
} from '../lib/format';
import { openNavigation, openTel } from '../lib/liff';

export default function BeneficiaryView() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState<BeneficiaryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    api<BeneficiaryDetail>(`/beneficiaries/${id}`, { signal: ac.signal })
      .then(setB)
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setError(
          e instanceof ApiRequestError
            ? e.message
            : 'เปิดข้อมูลไม่สำเร็จ',
        );
      });
    return () => ac.abort();
  }, [id]);

  if (error) {
    return (
      <div className="screen">
        <div className="topbar">
          <button className="back" onClick={() => navigate(-1)}>
            ← กลับ
          </button>
        </div>
        <div className="alert error">{error}</div>
      </div>
    );
  }

  if (!b) return <div className="center">กำลังโหลด…</div>;

  return (
    <div className="screen">
      <div className="topbar">
        <button className="back" onClick={() => navigate(-1)}>
          ← กลับ
        </button>
      </div>

      <div className="card">
        <div className="list-title" style={{ fontSize: 20 }}>
          {fullName(b)}
        </div>
        <div className="list-sub mono">{b.benCode}</div>
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className={`badge ${b.status === 'active' ? 'green' : ''}`}>
            {BENEFICIARY_STATUS_TH[b.status] ?? b.status}
          </span>
          {b.riskLevel && (
            <span className={`badge ${b.riskLevel === 'high' || b.riskLevel === 'critical' ? 'red' : 'amber'}`}>
              ความเสี่ยง{RISK_TH[b.riskLevel] ?? b.riskLevel}
            </span>
          )}
          {!b.consent.photoPublishAt && <span className="badge amber">ห้ามเผยแพร่ภาพ</span>}
        </div>
      </div>

      <div className="btn-row" style={{ marginBottom: 12 }}>
        <button
          className="secondary"
          disabled={!b.phone}
          onClick={() => b.phone && openTel(b.phone)}
        >
          📞 โทร
        </button>
        <button
          className="secondary"
          disabled={b.lat === null || b.lng === null}
          onClick={() => b.lat !== null && b.lng !== null && openNavigation(b.lat, b.lng)}
        >
          🗺️ นำทาง
        </button>
      </div>

      <div className="card">
        <h2>ข้อมูลทั่วไป</h2>
        <dl className="detail-list">
          <dt>เลขบัตร</dt>
          <dd className="mono">{maskNationalId(b.nationalIdLast4)}</dd>
          <dt>อายุ</dt>
          <dd>
            {ageFrom(b.birthDate)}
            {b.birthDate && <span className="muted small"> ({formatDate(b.birthDate)})</span>}
          </dd>
          <dt>ประเภท</dt>
          <dd>{b.category ? (CATEGORY_TH[b.category] ?? b.category) : '—'}</dd>
          <dt>เบอร์โทร</dt>
          <dd>{b.phone ?? '—'}</dd>
          <dt>ผู้ดูแล</dt>
          <dd>{b.primaryOfficerName ?? 'ยังไม่มีผู้ดูแล'}</dd>
        </dl>
      </div>

      <div className="card">
        <h2>ที่อยู่</h2>
        <p style={{ margin: '0 0 6px' }}>{b.addressLine ?? '—'}</p>
        <p className="muted small" style={{ margin: 0 }}>
          {b.areaName ?? 'ไม่ระบุพื้นที่'}
        </p>
        {b.locationNote && (
          <p className="small" style={{ marginBottom: 0 }}>
            จุดสังเกต: {b.locationNote}
          </p>
        )}
        {b.lat === null && (
          <p className="muted small" style={{ marginBottom: 0 }}>
            ยังไม่ได้ปักหมุดพิกัดบ้าน — จะบันทึกอัตโนมัติตอนเช็คอินครั้งแรก (เฟส 2)
          </p>
        )}
      </div>

      <div className="card">
        <h2>ครัวเรือน</h2>
        <dl className="detail-list">
          <dt>จำนวนสมาชิก</dt>
          <dd>{b.householdSize ?? '—'} คน</dd>
          <dt>รายได้/เดือน</dt>
          <dd>{formatSatang(b.monthlyIncome)}</dd>
        </dl>
        {b.householdMembers.length > 0 && (
          <ul style={{ margin: '10px 0 0', paddingInlineStart: 20 }} className="small">
            {b.householdMembers.map((m) => (
              <li key={m.id}>
                {m.fullName}
                {m.relation && ` (${m.relation})`}
                {m.isDependent && <span className="muted"> · ผู้พึ่งพิง</span>}
                {'healthNote' in m && m.healthNote && (
                  <>
                    <br />
                    <span className="muted">สุขภาพ: {m.healthNote}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {b.notes && (
        <div className="card">
          <h2>บันทึกเพิ่มเติม</h2>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }} className="small">
            {b.notes}
          </p>
        </div>
      )}

      <div className="alert info small">
        การบันทึกแบบสำรวจ เช็คอิน GPS และถ่ายรูปหน้างาน จะเปิดใช้ในเฟส 2
      </div>
    </div>
  );
}
